<template>
    <div>
        <ts-page-title
            title="Loan Transaction Summary"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: 'Loan Transaction Summary', active: true }
            ]"
        />

        <!-- ────────────────── FILTER PANEL ────────────────── -->
        <ts-panel>
            <ts-panel-wrapper class="tw-py-3">
                <div
                    class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none"
                    @click="filtersOpen = !filtersOpen"
                >
                    <div class="tw-flex tw-items-center tw-space-x-3 tw-flex-1 tw-min-w-0">
                        <i
                            class="fas tw-text-gray-400"
                            :class="filtersOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
                            style="width: 12px"
                        ></i>
                        <b class="tw-text-gray-700 tw-whitespace-nowrap">Filter Criteria</b>
                        <div v-if="activeChips.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1 tw-min-w-0">
                            <span
                                v-for="chip in activeChips"
                                :key="chip.key"
                                class="chip-pill"
                                :class="chip.tone || 'chip-default'"
                            >
                                <i v-if="chip.icon" :class="'fas ' + chip.icon + ' tw-mr-1'"></i>
                                {{ chip.label }}
                            </span>
                        </div>
                    </div>
                    <span class="tw-text-sm tw-text-blue-600 tw-whitespace-nowrap tw-ml-3">
                        {{ filtersOpen ? 'Collapse' : 'Edit filters' }}
                    </span>
                </div>

                <div v-show="filtersOpen" class="tw-mt-3">
                    <!-- Row 1: Branch / CO (hidden in Branch mode) / Currency / Tran Type -->
                    <div class="filter-grid">
                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Branch <span class="tw-text-gray-400 tw-font-normal tw-normal-case">(all if empty)</span></label>
                            <ts-branch-filter
                                @filter="value => (model.branch_id = value)"
                                :isPlaceholder="true"
                            />
                        </div>

                        <div class="filter-cell" style="grid-column: span 3" v-if="model.classified_by === 'co'">
                            <label class="filter-lbl">Credit Officer</label>
                            <Select
                                v-model="model.co_id"
                                multiple
                                :max-tag-count="1"
                                :filterable="true"
                                :filter-by-label="true"
                                placeholder="All COs"
                            >
                                <Option
                                    v-for="item in employees"
                                    :value="item.employee_id"
                                    :key="item.employee_id"
                                    :label="item.employee_name_en"
                                >
                                    {{ item.employee_name_en }}
                                </Option>
                            </Select>
                        </div>

                        <div class="filter-cell" :style="'grid-column: span ' + (model.classified_by === 'co' ? 2 : 5)">
                            <label class="filter-lbl">Currency <span class="text-danger">*</span></label>
                            <div class="pill-group">
                                <button
                                    v-for="c in currencies"
                                    :key="c.currency_id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.currency_id === c.currency_id }"
                                    @click="model.currency_id = c.currency_id"
                                >
                                    {{ c.currency_code }}
                                </button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Transaction Type</label>
                            <div class="pill-group">
                                <button
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': !model.tran_id }"
                                    @click="model.tran_id = null"
                                >All</button>
                                <button
                                    v-for="t in tranTypes"
                                    :key="t.id"
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.tran_id === t.id }"
                                    @click="model.tran_id = t.id"
                                >
                                    <span class="dot" :style="{ background: t.dot }"></span>
                                    {{ t.label }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2: Classified By / Date / Search + actions -->
                    <div class="filter-grid tw-mt-3 tw-items-end">
                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Classified by</label>
                            <div class="pill-group">
                                <button
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.classified_by === 'co' }"
                                    @click="onClassifiedChange('co')"
                                >
                                    <i class="fas fa-user tw-mr-1 tw-text-gray-400"></i> CO
                                </button>
                                <button
                                    type="button"
                                    class="pill"
                                    :class="{ 'pill-active': model.classified_by === 'branch' }"
                                    @click="onClassifiedChange('branch')"
                                >
                                    <i class="fas fa-building tw-mr-1 tw-text-gray-400"></i> Branch
                                </button>
                            </div>
                        </div>

                        <div
                            class="filter-cell"
                            :style="{ 'grid-column': model.date_preset === 'custom' ? 'span 6' : 'span 4' }"
                        >
                            <label class="filter-lbl">Date <span class="text-danger">*</span></label>
                            <div class="date-row">
                                <div class="pill-group">
                                    <button
                                        v-for="p in datePresets"
                                        :key="p.value"
                                        type="button"
                                        class="pill"
                                        :class="{ 'pill-active': model.date_preset === p.value }"
                                        @click="model.date_preset = p.value"
                                    >{{ p.label }}</button>
                                </div>
                                <div
                                    v-if="model.date_preset === 'custom'"
                                    class="date-picker-wrap"
                                >
                                    <DatePicker
                                        type="daterange"
                                        format="dd-MM-yyyy"
                                        placeholder="Select range (max 31 days)"
                                        style="width: 100%"
                                        @on-change="onCustomRangeChange"
                                    ></DatePicker>
                                </div>
                                <div v-else class="date-preview">{{ presetPreview }}</div>
                            </div>
                        </div>

                        <div
                            class="filter-cell"
                            :style="{ 'grid-column': model.date_preset === 'custom' ? 'span 3' : 'span 5' }"
                        >
                            <label class="filter-lbl">Search</label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input
                                        type="text"
                                        class="form-control search-input"
                                        v-model="searchQuery"
                                        placeholder="Loan #, customer code / name, or CO name"
                                        @keyup.enter="onSearch"
                                    />
                                    <button
                                        v-if="searchQuery"
                                        type="button"
                                        class="search-clear"
                                        title="Clear search"
                                        @click.prevent="onClearSearch"
                                    ><i class="fas fa-times-circle"></i></button>
                                </div>
                                <button
                                    type="button"
                                    class="search-btn"
                                    :disabled="loading"
                                    @click.prevent="onSearch"
                                >
                                    <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Search</span>
                                </button>
                                <button
                                    type="button"
                                    class="reset-btn"
                                    @click.prevent="onReset"
                                    title="Reset all filters"
                                ><i class="fas fa-undo"></i></button>
                            </div>
                        </div>
                    </div>
                </div>
            </ts-panel-wrapper>
        </ts-panel>

        <!-- ────────────────── SUMMARY CARDS (read-only) ────────────────── -->
        <div class="summary-grid tw-mt-3">
            <div
                v-for="card in summaryCards"
                :key="card.key"
                class="summary-card"
                :class="'summary-card--' + card.tone"
            >
                <div class="summary-card__header">
                    <div class="summary-card__icon">
                        <i :class="'fas ' + card.icon"></i>
                    </div>
                    <div class="summary-card__label">{{ card.label }}</div>
                    <div class="summary-card__count">
                        <template v-if="summaryLoading">…</template>
                        <template v-else>{{ card.count.toLocaleString() }}</template>
                    </div>
                </div>
                <div class="summary-card__value">
                    <template v-if="summaryLoading">
                        <span class="tw-text-gray-300">—</span>
                    </template>
                    <template v-else>
                        <span class="summary-card__amount">{{ fmt(card.total) }}</span>
                        <span class="summary-card__currency">{{ currencyCode }}</span>
                    </template>
                </div>
                <div
                    v-if="card.key === 'disbursement' && !summaryLoading"
                    class="summary-card__sub"
                    :title="'Principal only (excludes admin + insurance fees)'"
                >
                    <span class="summary-card__sub-label">principal</span>
                    <span class="summary-card__sub-value">{{ fmt(card.principal) }}</span>
                    <span class="summary-card__sub-currency">{{ currencyCode }}</span>
                </div>
            </div>
        </div>

        <!-- ────────────────── RESULTS CARD ────────────────── -->
        <div class="results-card tw-mt-3">
            <div class="results-toolbar">
                <div class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-flex-wrap">
                    <template v-if="loading">
                        <span class="tw-text-gray-500">
                            <i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Loading…
                        </span>
                    </template>
                    <template v-else-if="meta.total === null">
                        <span class="tw-text-gray-400">
                            <i class="fas fa-info-circle tw-mr-1"></i> No search yet
                        </span>
                    </template>
                    <template v-else-if="meta.total === 0">
                        <span class="tw-text-gray-500">
                            <i class="far fa-folder-open tw-mr-1"></i> No aggregates found
                        </span>
                    </template>
                    <template v-else>
                        <span class="tw-font-bold tw-text-gray-800 tw-text-base">
                            {{ meta.total.toLocaleString() }} rows
                        </span>
                        <span class="tw-text-gray-500">
                            · Page {{ meta.current_page }} of {{ meta.last_page }}
                        </span>
                    </template>

                </div>

                <div class="export-group" title="Export the current filters to xlsx">
                    <select v-model="model.excel_template" class="export-select">
                        <option value="standard">Standard xlsx</option>
                        <option value="raw-data">Raw Data xlsx</option>
                    </select>
                    <button
                        type="button"
                        class="export-btn"
                        :disabled="exporting"
                        @click.prevent="exportExcel"
                    >
                        <i class="fas fa-circle-notch fa-spin" v-if="exporting"></i>
                        <i class="far fa-file-excel" v-else></i>
                        <span class="tw-ml-1">Export</span>
                    </button>
                </div>
            </div>

            <div
                class="results-body tw-relative"
                :style="{ minHeight: hasRows ? '0px' : emptyBodyHeight + 'px' }"
            >
                <Spin size="large" fix v-if="loading"></Spin>

                <div v-if="!loading && meta.total === null" class="empty-state">
                    <i class="fas fa-search tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">Ready when you are — click <b>Search</b> above.</div>
                </div>

                <div v-else-if="!loading && meta.total === 0" class="empty-state">
                    <i class="far fa-folder-open tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">No aggregates match your filters.</div>
                    <div class="tw-text-xs tw-text-gray-400">Try a wider date range or clear the search.</div>
                </div>

                <Table
                    v-else
                    :columns="columns"
                    :data="rows"
                    border
                    stripe
                    size="small"
                    :height="tableHeight"
                    show-summary
                    :summary-method="summaryMethod"
                    @on-sort-change="onSortChange"
                ></Table>
            </div>

            <div v-if="meta.total > 0" class="results-pagination">
                <div class="pagination-left">
                    <span class="pagination-lbl">Per page:</span>
                    <Select
                        v-model="perPage"
                        @on-change="onPerPageChange"
                        style="width: 80px"
                        size="small"
                    >
                        <Option :value="10">10</Option>
                        <Option :value="30">30</Option>
                        <Option :value="50">50</Option>
                        <Option :value="100">100</Option>
                    </Select>
                    <span class="pagination-divider">|</span>
                    <span class="pagination-lbl">
                        Showing <b>{{ rangeStart.toLocaleString() }}</b>–<b>{{ rangeEnd.toLocaleString() }}</b>
                        of <b>{{ meta.total.toLocaleString() }}</b>
                    </span>
                </div>
                <Page
                    :current="meta.current_page"
                    :total="meta.total || 0"
                    :page-size="perPage"
                    @on-change="onPageChange"
                    size="small"
                    show-elevator
                ></Page>
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce, trim } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

const TRAN_TYPE_META = {
    10: { label: 'Disbursement', color: '#3b82f6' },
    11: { label: 'Repayment',    color: '#10b981' },
    17: { label: 'Recovery',     color: '#f59e0b' }
}

export default {
    name: 'loanTransactionSummaryV3',
    data () {
        return {
            filtersOpen: true,
            loading: false,
            exporting: false,
            errors: new Errors(),
            searchQuery: '',
            perPage: 30,
            viewportHeight: window.innerHeight,
            summaryLoading: false,
            summary: {
                all:          { count: 0, total: '0', principal: '0' },
                disbursement: { count: 0, total: '0', principal: '0' },
                repayment:    { count: 0, total: '0', principal: '0' },
                recovery:     { count: 0, total: '0', principal: '0' }
            },
            totals: {
                num_tran: 0,
                total_amount:    '0',
                principal:       '0',
                interest:        '0',
                admin_fee:       '0',
                insurance_fee:   '0',
                monthly_fee:     '0',
                rescue_fee:      '0',
                overdue_penalty: '0',
                payoff_penalty:  '0'
            },
            rows: [],
            meta: { current_page: 1, last_page: 1, total: null },
            datePresets: [
                { value: 'today',      label: 'Today' },
                { value: 'yesterday',  label: 'Yesterday' },
                { value: 'custom',     label: 'Custom' }
            ],
            tranTypes: [
                { id: 10, label: 'Disbursement', dot: '#3b82f6' },
                { id: 11, label: 'Repayment',    dot: '#10b981' },
                { id: 17, label: 'Recovery',     dot: '#f59e0b' }
            ],
            model: {
                branch_id: [],
                co_id: [],
                currency_id: null,
                tran_id: null,
                classified_by: 'co',
                date_preset: 'today',
                date_from: null,
                date_to: null,
                search: null,
                sort: 'branch_name_en',
                sort_dir: 'asc',
                page: 1,
                perPage: 30,
                excel_template: 'standard'
            }
        }
    },
    created () {
        this.model.currency_id = this.$store.state.authUser.sys_setting[0].default_currency_id
    },
    computed: {
        ...mapState('report/resource', ['employees', 'currencies']),

        currencyCode () {
            if (!this.model.currency_id) return ''
            const c = (this.currencies || []).find(c => c.currency_id === this.model.currency_id)
            return c ? c.currency_code : ''
        },
        isKh () {
            const locale = (this.$i18n && this.$i18n.locale) || localStorage.getItem('langCode')
            return locale === 'kh'
        },

        presetPreview () {
            const today = moment()
            switch (this.model.date_preset) {
                case 'today':      return today.format('ddd, DD MMM YYYY')
                case 'yesterday':  return today.clone().subtract(1, 'day').format('ddd, DD MMM YYYY')
                case 'custom':
                    if (this.model.date_from && this.model.date_to) {
                        return this.model.date_from + ' → ' + this.model.date_to
                    }
                    return null
            }
            return ''
        },

        activeChips () {
            const chips = []
            if (this.model.branch_id && this.model.branch_id.length) {
                chips.push({ key: 'branch', icon: 'fa-building',
                    label: this.model.branch_id.length + ' branch' + (this.model.branch_id.length > 1 ? 'es' : ''),
                    tone: 'chip-branch' })
            } else {
                chips.push({ key: 'branch', icon: 'fa-building', label: 'All branches', tone: 'chip-branch' })
            }
            if (this.model.currency_id) {
                const cur = this.currencies.find(c => c.currency_id === this.model.currency_id)
                if (cur) chips.push({ key: 'currency', label: cur.currency_code, tone: 'chip-currency' })
            }
            chips.push({ key: 'classified',
                icon: this.model.classified_by === 'co' ? 'fa-user' : 'fa-building',
                label: 'By ' + (this.model.classified_by === 'co' ? 'CO' : 'Branch'),
                tone: 'chip-default' })
            if (this.presetPreview) {
                chips.push({ key: 'date', icon: 'fa-calendar-alt', label: this.presetPreview, tone: 'chip-date' })
            }
            if (this.model.tran_id) {
                const t = TRAN_TYPE_META[this.model.tran_id]
                chips.push({ key: 'type', label: t && t.label, tone: 'chip-type' })
            }
            if (this.model.co_id && this.model.co_id.length && this.model.classified_by === 'co') {
                chips.push({ key: 'co', icon: 'fa-user',
                    label: this.model.co_id.length + ' CO' + (this.model.co_id.length > 1 ? 's' : ''),
                    tone: 'chip-co' })
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                chips.push({ key: 'search', icon: 'fa-search', label: '"' + this.searchQuery.trim() + '"', tone: 'chip-search' })
            }
            return chips.filter(c => c.label)
        },

        tableHeight () {
            const chrome = this.filtersOpen ? 601 : 471
            const h = this.viewportHeight - chrome
            return Math.max(280, h)
        },
        emptyBodyHeight () {
            const PAGINATION_ROW = 52
            return this.tableHeight + PAGINATION_ROW
        },
        hasRows () {
            return !this.loading && this.meta.total > 0
        },
        rangeStart () {
            if (!this.meta.total) return 0
            return (this.meta.current_page - 1) * this.perPage + 1
        },
        rangeEnd () {
            if (!this.meta.total) return 0
            return Math.min(this.meta.current_page * this.perPage, this.meta.total)
        },

        summaryCards () {
            return [
                { key: 'all',          icon: 'fa-layer-group',      tone: 'all',
                  label: 'All transactions',
                  count: this.summary.all.count, total: this.summary.all.total },
                { key: 'disbursement', icon: 'fa-arrow-up',         tone: 'disbursement',
                  label: 'Disbursement',
                  count: this.summary.disbursement.count,
                  total: this.summary.disbursement.total,
                  principal: this.summary.disbursement.principal },
                { key: 'repayment',    icon: 'fa-arrow-down',       tone: 'repayment',
                  label: 'Repayment',
                  count: this.summary.repayment.count, total: this.summary.repayment.total },
                { key: 'recovery',     icon: 'fa-hand-holding-usd', tone: 'recovery',
                  label: 'Recovery',
                  count: this.summary.recovery.count, total: this.summary.recovery.total }
            ]
        },

        columns () {
            const isCo = this.model.classified_by === 'co'
            const cols = [
                {
                    title: 'Branch',
                    key: 'branch_name_en',
                    width: 100,
                    fixed: 'left',
                    align: 'center',
                    sortable: 'custom',
                    render: (h, params) =>
                        h('span', { class: 'branch-code' }, params.row.branch_code || '—')
                }
            ]
            if (isCo) {
                cols.push({
                    title: 'CO',
                    key: 'co_name_en',
                    minWidth: 180,
                    fixed: 'left',
                    sortable: 'custom',
                    render: (h, params) => h('div', { class: 'code-name-cell' }, [
                        h('span', { class: 'code-chip' }, params.row.card_id || '—'),
                        h('span', { class: 'name-text' }, ' ' +
                            (this.isKh
                                ? (params.row.co_name_kh || params.row.co_name_en)
                                : (params.row.co_name_en || params.row.co_name_kh)))
                    ])
                })
            }
            cols.push({
                title: 'Type',
                key: 'tran_type',
                width: 140,
                sortable: 'custom',
                align: 'center',
                render: (h, params) => {
                    const meta = TRAN_TYPE_META[params.row.tran_type_id]
                        || { label: params.row.tran_type, color: '#94a3b8' }
                    return h('span', {
                        class: 'type-chip',
                        style: {
                            color: meta.color,
                            background: this.hexToBg(meta.color),
                            borderColor: this.hexToBorder(meta.color)
                        }
                    }, [
                        h('span', { class: 'type-dot', style: { background: meta.color } }),
                        meta.label
                    ])
                }
            })
            cols.push({
                title: '# Txn',
                key: 'num_tran',
                width: 100,
                align: 'right',
                sortable: 'custom',
                render: (h, params) => h('span', { class: 'tw-font-semibold tw-text-gray-800' },
                    (params.row.num_tran || 0).toLocaleString())
            })
            cols.push({
                title: 'Total',
                key: 'total_amount',
                width: 140,
                align: 'right',
                sortable: 'custom',
                render: (h, params) => h('span', { class: 'tw-font-semibold tw-text-gray-800' },
                    this.fmt(params.row.total_amount))
            })
            cols.push({ title: 'Principal',       key: 'principal',       width: 130, align: 'right', render: this.moneyCell('principal') })
            cols.push({ title: 'Interest',        key: 'interest',        width: 115, align: 'right', render: this.moneyCell('interest') })
            cols.push({ title: 'Admin',           key: 'admin_fee',       width: 110, align: 'right', render: this.moneyCell('admin_fee') })
            cols.push({ title: 'Insurance',       key: 'insurance_fee',   width: 125, align: 'right', render: this.moneyCell('insurance_fee') })
            cols.push({ title: 'Monthly',         key: 'monthly_fee',     width: 115, align: 'right', render: this.moneyCell('monthly_fee') })
            cols.push({ title: 'Rescue',          key: 'rescue_fee',      width: 110, align: 'right', render: this.moneyCell('rescue_fee') })
            cols.push({ title: 'Overdue Penalty', key: 'overdue_penalty', width: 165, align: 'right', render: this.moneyCell('overdue_penalty') })
            cols.push({ title: 'Payoff Penalty',  key: 'payoff_penalty',  minWidth: 165, align: 'right', render: this.moneyCell('payoff_penalty') })
            return cols
        }
    },
    methods: {
        ...mapActions('report/resource', ['getEmployee', 'getCurrency']),

        hexToBg (hex) {
            const map = { '#3b82f6': '#eff6ff', '#10b981': '#ecfdf5', '#f59e0b': '#fef3c7' }
            return map[hex] || '#f3f4f6'
        },
        hexToBorder (hex) {
            const map = { '#3b82f6': '#dbeafe', '#10b981': '#d1fae5', '#f59e0b': '#fde68a' }
            return map[hex] || '#e5e7eb'
        },

        fmt (v) {
            const n = parseFloat(v)
            if (isNaN(n) || n === 0) return '—'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        moneyCell (key) {
            return (h, params) => {
                const val = params.row[key]
                const n = parseFloat(val)
                if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
                return h('span', this.fmt(val))
            }
        },

        summaryMethod ({ columns }) {
            const t = this.totals
            const sums = {}
            const money = k => this.fmtStrict(t[k])

            columns.forEach((col, idx) => {
                if (idx === 0) {
                    sums[col.key] = { key: col.key, value: 'TOTAL' }
                    return
                }
                let value = ''
                switch (col.key) {
                    case 'num_tran':        value = (t.num_tran || 0).toLocaleString(); break
                    case 'total_amount':    value = money('total_amount'); break
                    case 'principal':       value = money('principal'); break
                    case 'interest':        value = money('interest'); break
                    case 'admin_fee':       value = money('admin_fee'); break
                    case 'insurance_fee':   value = money('insurance_fee'); break
                    case 'monthly_fee':     value = money('monthly_fee'); break
                    case 'rescue_fee':      value = money('rescue_fee'); break
                    case 'overdue_penalty': value = money('overdue_penalty'); break
                    case 'payoff_penalty':  value = money('payoff_penalty'); break
                    default:                value = ''
                }
                sums[col.key] = { key: col.key, value }
            })
            return sums
        },
        fmtStrict (v) {
            const n = parseFloat(v)
            if (isNaN(n)) return '0'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },

        onCustomRangeChange (range) {
            if (Array.isArray(range) && range[0] && range[1]) {
                this.model.date_from = range[0]
                this.model.date_to   = range[1]
            } else {
                this.model.date_from = null
                this.model.date_to   = null
            }
        },

        onClassifiedChange (mode) {
            if (this.model.classified_by === mode) return
            this.model.classified_by = mode
            // Branch mode → clear CO filter and reset sort if it was co_name_en
            if (mode === 'branch') {
                this.model.co_id = []
                if (this.model.sort === 'co_name_en') {
                    this.model.sort = 'branch_name_en'
                    this.model.sort_dir = 'asc'
                }
            }
        },

        buildParams () {
            const p = {
                branch_id:      this.model.branch_id,
                co_id:          this.model.co_id,
                currency_id:    this.model.currency_id,
                tran_id:        this.model.tran_id,
                classified_by:  this.model.classified_by,
                date_preset:    this.model.date_preset,
                sort:           this.model.sort,
                sort_dir:       this.model.sort_dir,
                page:           this.model.page,
                perPage:        this.perPage,
                excel_template: this.model.excel_template
            }
            if (this.model.date_preset === 'custom') {
                p.date_from = this.model.date_from
                p.date_to   = this.model.date_to
            }
            if (this.searchQuery && this.searchQuery.trim().length >= 2) {
                p.search = this.searchQuery.trim()
            }
            return p
        },

        fetchData () {
            this.errors = new Errors()
            this.loading = true
            this.summaryLoading = true

            this.$store
                .dispatch('report/creditOperation/loanTransactionSummaryV3', this.buildParams())
                .then(response => {
                    const data    = response.data || []
                    const meta    = response.meta || response
                    const summary = response.summary || null
                    const totals  = response.totals  || null

                    this.rows = data
                    this.meta = {
                        current_page: meta.current_page || 1,
                        last_page:    meta.last_page    || 1,
                        total:        meta.total ?? 0
                    }

                    if (summary) {
                        const empty = { count: 0, total: '0', principal: '0' }
                        this.summary = {
                            all:          Object.assign({}, empty, summary.all          || {}),
                            disbursement: Object.assign({}, empty, summary.disbursement || {}),
                            repayment:    Object.assign({}, empty, summary.repayment    || {}),
                            recovery:     Object.assign({}, empty, summary.recovery     || {})
                        }
                    }
                    if (totals) {
                        this.totals = Object.assign(
                            { num_tran: 0, total_amount: '0', principal: '0', interest: '0', admin_fee: '0',
                              insurance_fee: '0', monthly_fee: '0', rescue_fee: '0',
                              overdue_penalty: '0', payoff_penalty: '0' },
                            totals
                        )
                    }
                })
                .catch(error => {
                    this.notice({ type: 'error', text: (error && error.message) || 'Failed to load report' })
                    this.errors = new Errors((error && error.errors) || {})
                    this.rows = []
                })
                .finally(() => {
                    this.loading = false
                    this.summaryLoading = false
                })
        },

        onSearch () {
            if (this.model.date_preset === 'custom' &&
                (!this.model.date_from || !this.model.date_to)) {
                this.notice({ type: 'warning', text: 'Please pick a date range before searching.' })
                return
            }
            this.model.page = 1
            this.filtersOpen = false
            this.fetchData()
        },

        onClearSearch () {
            const hadSearch = !!(this.searchQuery && this.searchQuery.trim())
            this.searchQuery = ''
            if (hadSearch && this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        },

        onReset () {
            this.searchQuery = ''
            this.model.co_id = []
            this.model.tran_id = null
            this.model.classified_by = 'co'
            this.model.date_preset = 'today'
            this.model.date_from = null
            this.model.date_to = null
            this.model.sort = 'branch_name_en'
            this.model.sort_dir = 'asc'
            this.model.page = 1
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
        },

        onSortChange ({ key, order }) {
            if (order === 'normal') {
                this.model.sort = 'branch_name_en'
                this.model.sort_dir = 'asc'
            } else {
                this.model.sort = key
                this.model.sort_dir = order === 'asc' ? 'asc' : 'desc'
            }
            this.model.page = 1
            this.fetchData()
        },

        onPageChange (page) {
            this.model.page = page
            this.fetchData()
        },
        onPerPageChange (v) {
            this.perPage = v
            this.model.page = 1
            this.fetchData()
        },

        exportExcel () {
            this.errors = new Errors()
            this.exporting = true

            this.$store
                .dispatch('report/creditOperation/loanTransactionSummaryV3ExportExcel', this.buildParams())
                .then(response => {
                    const filename = trim(
                        response.headers['content-disposition']
                            .substring(response.headers['content-disposition'].indexOf('filename'))
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let msg = 'Export failed'
                    try {
                        const parsed = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
                        msg = parsed.message || msg
                        this.errors = new Errors(parsed.errors || {})
                    } catch (e) { /* not JSON */ }
                    this.notice({ type: 'error', text: msg })
                })
                .finally(() => { this.exporting = false })
        },

        notice (not) {
            this.$Notice[not.type]({
                title: 'LOAN TRANSACTION SUMMARY',
                desc: not.text
            })
        }
    },
    watch: {
        'model.branch_id': debounce(function (value) {
            this.model.co_id = []
            this.getEmployee({ branch_id: value })
        }, 500)
    },
    mounted () {
        this._onResize = debounce(() => { this.viewportHeight = window.innerHeight }, 100)
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy () {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    },
    beforeRouteEnter (to, from, next) {
        next(vm => { vm.getCurrency() })
    }
}
</script>

<style scoped>
/* ── Filter grid ───────────────────────────────────────────────── */
.filter-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 12px;
    align-items: end;
}
.filter-cell { min-width: 0; }
.filter-lbl {
    display: block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    margin-bottom: 4px;
    height: 15px;
}
::v-deep .ivu-select-selection { min-height: 34px; }
::v-deep .ivu-input { height: 34px; }
::v-deep .ts-form-btn { height: 34px; }

/* ── Pill selector ─────────────────────────────────────────────── */
.pill-group {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 3px;
    padding: 3px;
    background: #f3f4f6;
    border-radius: 6px;
    height: 34px;
    box-sizing: border-box;
    overflow-x: auto;
    max-width: 100%;
}
.pill {
    background: transparent;
    border: none;
    padding: 4px 10px;
    font-size: 12px;
    color: #4b5563;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    line-height: 1.2;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
}
.pill:hover { background: #e5e7eb; }
.pill-active {
    background: #ffffff;
    color: #111827;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}
.pill .dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 5px;
}
.date-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.date-picker-wrap {
    flex: 1 1 220px;
    min-width: 220px;
}
.date-preview {
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
    padding-left: 8px;
    line-height: 34px;
    white-space: nowrap;
}

/* ── Chip pills ──────────────────────────────────────────────── */
.chip-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    font-size: 11px;
    border-radius: 10px;
    background: #f3f4f6;
    color: #374151;
    line-height: 1.5;
    white-space: nowrap;
}
.chip-default  { background: #f3f4f6; color: #374151; }
.chip-branch   { background: #eef2ff; color: #4338ca; }
.chip-currency { background: #ecfdf5; color: #047857; }
.chip-date     { background: #fef3c7; color: #92400e; }
.chip-type     { background: #dbeafe; color: #1e40af; }
.chip-co       { background: #f3e8ff; color: #6b21a8; }
.chip-search   { background: #fce7f3; color: #9d174d; }

@media (max-width: 991px) {
    .filter-grid > .filter-cell {
        grid-column: span 12 !important;
    }
}

/* ── Search group ─────────────────────────────────────────────── */
.search-group {
    display: flex;
    align-items: stretch;
    height: 34px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;
    background: #ffffff;
    transition: border-color 0.15s, box-shadow 0.15s;
}
.search-group:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
.search-input-wrap {
    position: relative;
    flex: 1;
    display: flex;
    align-items: stretch;
    min-width: 0;
}
.search-input {
    flex: 1;
    border: none !important;
    height: 100% !important;
    padding: 0 32px 0 12px;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #111827;
    min-width: 0;
}
.search-input:focus { box-shadow: none !important; }
.search-clear {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    padding: 4px;
    font-size: 14px;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: color 0.12s, background 0.12s;
}
.search-clear:hover { color: #4b5563; background: #f3f4f6; }
.search-btn {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: background 0.15s;
    white-space: nowrap;
}
.search-btn:hover:not(:disabled) { background: #2563eb; }
.search-btn:disabled { background: #93c5fd; cursor: wait; }
.reset-btn {
    background: #f9fafb;
    color: #6b7280;
    border: none;
    border-left: 1px solid #e5e7eb;
    padding: 0 12px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}
.reset-btn:hover { background: #f3f4f6; color: #374151; }

/* ── Results card ─────────────────────────────────────────────── */
.results-card {
    background: #ffffff;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}
.results-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;
    background: #ffffff;
    gap: 8px;
    flex-wrap: wrap;
}
.results-body { min-height: 120px; }
.results-pagination {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
    gap: 8px;
    min-height: 52px;
}
.pagination-left {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 28px;
    line-height: 28px;
    font-size: 13px;
    color: #6b7280;
}
.pagination-lbl {
    display: inline-flex;
    align-items: center;
    height: 28px;
    line-height: 28px;
    white-space: nowrap;
}
.pagination-lbl b { color: #111827; font-weight: 600; margin: 0 2px; }
.pagination-divider { color: #d1d5db; line-height: 28px; }
.pagination-left ::v-deep .ivu-select-small {
    display: inline-flex;
    align-items: center;
}
.pagination-left ::v-deep .ivu-select-small .ivu-select-selection {
    display: flex;
    align-items: center;
}
.pagination-left ::v-deep .ivu-select-small .ivu-icon {
    top: 50% !important;
    transform: translateY(-50%);
    margin-top: 0 !important;
}

/* ── Summary cards ────────────────────────────────────────────── */
.summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}
@media (max-width: 991px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) { .summary-grid { grid-template-columns: 1fr; } }
.summary-card {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 14px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-top: 3px solid;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 110px;
}
.summary-card__header { display: flex; align-items: center; gap: 8px; }
.summary-card__icon {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    font-size: 11px;
    flex-shrink: 0;
}
.summary-card__label {
    flex: 1;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.summary-card__count {
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    padding: 1px 6px;
    background: #f3f4f6;
    border-radius: 10px;
    line-height: 1.4;
}
.summary-card__value {
    display: flex;
    align-items: baseline;
    gap: 6px;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
}
.summary-card__amount {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}
.summary-card__currency {
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.03em;
    flex-shrink: 0;
}
.summary-card__sub {
    display: flex;
    align-items: baseline;
    gap: 4px;
    padding-top: 6px;
    border-top: 1px dashed #e5e7eb;
    font-size: 11px;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
}
.summary-card__sub-label {
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-weight: 600;
    font-size: 10px;
}
.summary-card__sub-value {
    color: #374151;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1;
}
.summary-card__sub-currency {
    color: #9ca3af;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.03em;
    flex-shrink: 0;
}
.summary-card--all          { border-top-color: #6366f1; }
.summary-card--all          .summary-card__icon { background: #eef2ff; color: #4338ca; }
.summary-card--disbursement { border-top-color: #3b82f6; }
.summary-card--disbursement .summary-card__icon { background: #eff6ff; color: #1e40af; }
.summary-card--repayment    { border-top-color: #10b981; }
.summary-card--repayment    .summary-card__icon { background: #ecfdf5; color: #047857; }
.summary-card--recovery     { border-top-color: #f59e0b; }
.summary-card--recovery     .summary-card__icon { background: #fef3c7; color: #92400e; }

/* ── Table header + footer ────────────────────────────────────── */
::v-deep .ivu-table th {
    background: #f9fafb !important;
    height: 40px;
    color: #374151;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
::v-deep .ivu-table th .ivu-table-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
::v-deep .ivu-table-summary td {
    background: #f9fafb !important;
    font-weight: 700 !important;
    color: #111827 !important;
    border-top: 2px solid #d1d5db !important;
}
::v-deep .ivu-table-summary td .ivu-table-cell { font-size: 12px; }
::v-deep .ivu-table td .ivu-table-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 4px 12px;
}
::v-deep .ivu-table td { height: 34px; }
::v-deep .ivu-table-small td { height: 32px; }

/* ── Export controls ──────────────────────────────────────────── */
.export-group {
    display: inline-flex;
    align-items: stretch;
    height: 32px;
    border-radius: 5px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #d1d5db;
    background: #ffffff;
}
.export-select {
    border: 0;
    border-right: 1px solid #d1d5db;
    background: #ffffff;
    padding: 0 28px 0 12px;
    font-size: 12px;
    color: #374151;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    height: 100%;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%236b7280' d='M5 6L0 0h10z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 10px 6px;
}
.export-select:hover { background-color: #f9fafb; }
.export-btn {
    background: #10b981;
    color: white;
    border: none;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: background 0.15s;
    white-space: nowrap;
}
.export-btn:hover:not(:disabled) { background: #059669; }
.export-btn:disabled {
    background: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
}

.compare-link {
    color: #6b7280;
    font-size: 12px;
    text-decoration: none;
    transition: color 0.15s;
}
.compare-link:hover { color: #3b82f6; text-decoration: underline; }

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    height: 100%;
    position: absolute;
    inset: 0;
}
</style>

<!-- Unscoped: render-function-created elements don't receive data-v-xxx. -->
<style>
.code-name-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
}
.code-chip {
    display: inline-block;
    min-width: 68px;
    padding: 0 4px;
    font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    text-align: center;
    letter-spacing: 0.02em;
    line-height: 18px;
    white-space: nowrap;
    flex-shrink: 0;
}
.name-text {
    color: #111827;
    font-size: 12px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}
.type-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0 8px;
    height: 20px;
    font-size: 11px;
    font-weight: 600;
    border-radius: 10px;
    border: 1px solid;
    letter-spacing: 0.02em;
    white-space: nowrap;
}
.type-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}
.branch-code {
    display: inline-block;
    padding: 1px 6px;
    font-size: 11px;
    font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #4338ca;
    background: #eef2ff;
    border: 1px solid #e0e7ff;
    border-radius: 3px;
    letter-spacing: 0.02em;
}
</style>
